import axios from 'axios';
import { offlineQueue } from './offlineQueue';

const baseURL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'http://localhost:5001/api';

// Create axios instance with offline support
const offlineApi = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to handle offline scenarios
offlineApi.interceptors.request.use(
  async (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Check if offline and request is a write operation
    if (!navigator.onLine && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
      // Queue the request
      await offlineQueue.addTask(
        config.url!,
        config.method!.toUpperCase(),
        config.headers as Record<string, string>,
        config.data
      );

      // Return a mock response
      return Promise.reject({
        response: {
          status: 202,
          data: {
            queued: true,
            message: 'Task queued for sync when online',
            tempId: `temp_${Date.now()}`
          }
        },
        config
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
offlineApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If network error and it's a write operation, queue it
    if (!error.response && error.config && ['post', 'put', 'patch', 'delete'].includes(error.config.method?.toLowerCase())) {
      await offlineQueue.addTask(
        error.config.url,
        error.config.method.toUpperCase(),
        error.config.headers,
        error.config.data
      );

      return {
        status: 202,
        data: {
          queued: true,
          message: 'Task queued for sync when online',
          tempId: `temp_${Date.now()}`
        }
      };
    }

    return Promise.reject(error);
  }
);

// Offline-aware API methods
export const offlineApiService = {
  // Generic HTTP methods
  get: async (url: string, config?: any) => {
    const response = await offlineApi.get(url, config);
    return response;
  },

  post: async (url: string, data?: any, config?: any) => {
    const response = await offlineApi.post(url, data, config);
    return response;
  },

  put: async (url: string, data?: any, config?: any) => {
    const response = await offlineApi.put(url, data, config);
    return response;
  },

  patch: async (url: string, data?: any, config?: any) => {
    const response = await offlineApi.patch(url, data, config);
    return response;
  },

  delete: async (url: string, config?: any) => {
    const response = await offlineApi.delete(url, config);
    return response;
  },

  // Tasks
  createTask: async (gardenId: string, task: any) => {
    const response = await offlineApi.post(`/gardens/${gardenId}/tasks`, task);
    return response.data;
  },

  updateTaskStatus: async (taskId: string, status: string) => {
    const response = await offlineApi.patch(`/tasks/${taskId}/status`, { status });
    return response.data;
  },

  // Plants
  addPlant: async (gardenId: string, plant: any) => {
    const response = await offlineApi.post(`/gardens/${gardenId}/plants`, plant);
    return response.data;
  },

  updatePlantCare: async (plantId: string, care: any) => {
    const response = await offlineApi.patch(`/plants/${plantId}/care`, care);
    return response.data;
  },

  // Harvests
  logHarvest: async (plantId: string, harvest: any) => {
    const response = await offlineApi.post(`/plants/${plantId}/harvests`, harvest);
    return response.data;
  },

  // Gardens
  getGardens: async () => {
    const response = await offlineApi.get('/gardens');
    return response.data;
  },

  // Sync pending tasks
  syncOfflineTasks: async () => {
    const results = await offlineQueue.syncPendingTasks();
    return results;
  }
};

export default offlineApi;